import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { useMemo } from 'react'

import { axiosInstance } from '../../../helpers'
import {
    AttributeSummary,
    AuditAttribute,
    AuditAttributeMismatchResponse,
    AuditResidentTerm,
    AuditResults,
    PaginatedResponse,
    TermStatusFilter,
} from '../types'

export interface UseAuditResultsOptions {
    auditId: number
    enabled?: boolean
    termStatusFilter?: TermStatusFilter
    page?: number
    pageSize?: number
    mismatchAttributeId?: number | null
    mismatchEnabled?: boolean
}

export interface UpdateAuditAttributeValueParams {
    residentTermId: number
    attributeId: number
    documentValue: string | null
}

export interface SaveReviewNoteParams {
    residentTermId: number
    attributeId: number
    note: string | null
}

export interface ReviewNoteResponse {
    review_note: string | null
    review_note_author_name: string | null
    review_note_at: string | null
}

type AttributeSummaryResponse =
    | AttributeSummary[]
    | { results: AttributeSummary[] }
    | { attributes: AttributeSummary[] }

type AuditResultsResponse =
    | AuditResults
    | (PaginatedResponse<AuditResidentTerm> &
          Partial<Pick<AuditResults, 'attributes' | 'rent_roll_file'>>)

const normalizeAttributeSummary = (
    data: AttributeSummaryResponse | undefined,
): AttributeSummary[] => {
    if (Array.isArray(data)) {
        return data
    }

    if (data && typeof data === 'object') {
        if ('results' in data && Array.isArray(data.results)) {
            return data.results
        }

        if ('attributes' in data && Array.isArray(data.attributes)) {
            return data.attributes
        }
    }

    return []
}

const extractResidentTerms = (
    data: AuditResultsResponse | undefined,
): AuditResidentTerm[] | undefined => {
    if (!data) {
        return undefined
    }

    if ('results' in data) {
        return data.results
    }

    return data.resident_terms
}

const isPaginatedAuditResults = (
    data: AuditResultsResponse,
): data is PaginatedResponse<AuditResidentTerm> &
    Partial<Pick<AuditResults, 'attributes' | 'rent_roll_file'>> => {
    return 'results' in data && 'count' in data
}

export const useAuditResults = (options: UseAuditResultsOptions) => {
    const {
        auditId,
        enabled = true,
        termStatusFilter = 'CURRENT_ONLY',
        page = 1,
        pageSize = 50,
        mismatchAttributeId = null,
        mismatchEnabled = true,
    } = options
    const queryClient = useQueryClient()

    // Fetch audit results
    const { data: auditResultsData, isLoading, error, refetch } = useQuery<
        AxiosResponse<AuditResultsResponse>,
        Error
    >({
        queryKey: ['auditResults', auditId, termStatusFilter, page, pageSize],
        queryFn: () =>
            axiosInstance.get(
                `/lease-audit/${auditId}/results/resident-terms/`,
                {
                    params: {
                        page,
                        page_size: pageSize,
                        term_status_filter: termStatusFilter,
                    },
                },
            ),
        enabled: enabled && !!auditId,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    const {
        data: mismatchResultsData,
        isLoading: isLoadingMismatches,
        error: mismatchError,
        refetch: refetchMismatches,
    } = useQuery<AxiosResponse<AuditAttributeMismatchResponse>, Error>({
        queryKey: [
            'attributeMismatches',
            auditId,
            mismatchAttributeId,
            termStatusFilter,
        ],
        queryFn: () =>
            axiosInstance.get(
                `/lease-audit/${auditId}/results/attributes/mismatches/`,
                {
                    params: {
                        attribute_id: mismatchAttributeId,
                        term_status_filter: termStatusFilter,
                    },
                },
            ),
        enabled:
            enabled && !!auditId && !!mismatchAttributeId && mismatchEnabled,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    const auditResults = auditResultsData?.data
    const residentTerms = extractResidentTerms(auditResults)
    const totalCount =
        auditResults && 'count' in auditResults
            ? auditResults.count
            : residentTerms?.length ?? 0
    const totalPages = Math.ceil(totalCount / pageSize)
    const rentRollFile =
        auditResults && 'rent_roll_file' in auditResults
            ? auditResults.rent_roll_file
            : undefined
    const responseAttributes =
        auditResults && 'attributes' in auditResults
            ? auditResults.attributes
            : undefined

    const {
        data: attributeSummaryData,
        isLoading: isLoadingAttributeSummary,
        error: errorAttributeSummary,
        refetch: refetchAttributeSummary,
    } = useQuery<AxiosResponse<AttributeSummaryResponse>, Error>({
        queryKey: ['attributeSummary', auditId],
        queryFn: () =>
            axiosInstance.get(
                `/lease-audit/${auditId}/results/attributes/summary/`,
            ),
        enabled: enabled && !!auditId,
    })

    const attributeSummary = normalizeAttributeSummary(
        attributeSummaryData?.data,
    )

    const attributesFromSummary = useMemo<AuditAttribute[]>(() => {
        if (!attributeSummary.length) {
            return []
        }

        return attributeSummary
            .map((summary) => {
                const summaryId = summary.attribute_id ?? summary.id
                if (typeof summaryId !== 'number') {
                    return null
                }

                return {
                    id: summaryId,
                    name: summary.name,
                    display_order: summary.display_order,
                    should_extract: summary.should_extract,
                    data_type: summary.data_type,
                }
            })
            .filter(
                (attribute): attribute is AuditAttribute => attribute !== null,
            )
            .sort((a, b) => a.display_order - b.display_order)
    }, [attributeSummary])

    // Sort attributes by display_order
    const sortedAttributes = useMemo<AuditAttribute[]>(() => {
        if (responseAttributes?.length) {
            return [...responseAttributes].sort(
                (a, b) => a.display_order - b.display_order,
            )
        }

        return attributesFromSummary
    }, [auditResults?.attributes, attributesFromSummary])

    const mismatchPayload = mismatchResultsData?.data
    const mismatchResidentTerms = Array.isArray(mismatchPayload)
        ? mismatchPayload
        : mismatchPayload?.results ?? []
    const mismatchTotalCount = mismatchResidentTerms.length
    const mismatchTotalPages = 1

    const updateDocumentValueMutation = useMutation<
        unknown,
        Error,
        UpdateAuditAttributeValueParams,
        {
            previousAuditResults?: AxiosResponse<AuditResultsResponse>
            previousMismatch?: AxiosResponse<AuditAttributeMismatchResponse>
        }
    >({
        mutationFn: (params: UpdateAuditAttributeValueParams) =>
            axiosInstance.patch(
                `/lease-audit/${auditId}/audit-results/attribute-value/`,
                {
                    resident_term_id: params.residentTermId,
                    attribute_id: params.attributeId,
                    document_value: params.documentValue,
                },
            ),
        onMutate: async (params) => {
            const auditResultsQueryKey = [
                'auditResults',
                auditId,
                termStatusFilter,
                page,
                pageSize,
            ]

            await queryClient.cancelQueries({ queryKey: auditResultsQueryKey })

            const previous = queryClient.getQueryData<
                AxiosResponse<AuditResultsResponse>
            >(auditResultsQueryKey)

            const previousData = previous?.data
            const residentTermsToUpdate = extractResidentTerms(previousData)

            if (residentTermsToUpdate) {
                const updatedTerms = residentTermsToUpdate.map((term) => {
                    if (term.id !== params.residentTermId) {
                        return term
                    }

                    return {
                        ...term,
                        attribute_values: term.attribute_values.map((value) =>
                            value.attribute_id === params.attributeId
                                ? {
                                      ...value,
                                      document_value: params.documentValue,
                                      conflict_value: null,
                                  }
                                : value,
                        ),
                    }
                })

                const updatedData:
                    | AuditResultsResponse
                    | undefined = previousData
                    ? isPaginatedAuditResults(previousData)
                        ? {
                              ...previousData,
                              results: updatedTerms,
                          }
                        : {
                              ...previousData,
                              resident_terms: updatedTerms,
                          }
                    : previousData

                const updated = {
                    ...previous,
                    data: updatedData,
                }

                queryClient.setQueryData(auditResultsQueryKey, updated)
            }

            const mismatchQueryKey = [
                'attributeMismatches',
                auditId,
                mismatchAttributeId,
                termStatusFilter,
            ]

            await queryClient.cancelQueries({ queryKey: mismatchQueryKey })

            const previousMismatch =
                mismatchAttributeId !== null
                    ? queryClient.getQueryData<
                          AxiosResponse<AuditAttributeMismatchResponse>
                      >(mismatchQueryKey)
                    : undefined

            const mismatchData = previousMismatch?.data
            const mismatchResults = Array.isArray(mismatchData)
                ? mismatchData
                : mismatchData?.results ?? []

            if (mismatchResults.length) {
                const updatedMismatchTerms = mismatchResults.map((term) =>
                    term.id === params.residentTermId
                        ? {
                              ...term,
                              attribute_values: term.attribute_values.map(
                                  (value) =>
                                      value.attribute_id === params.attributeId
                                          ? {
                                                ...value,
                                                document_value:
                                                    params.documentValue,
                                                conflict_value: null,
                                            }
                                          : value,
                              ),
                          }
                        : term,
                )

                queryClient.setQueryData(mismatchQueryKey, {
                    ...previousMismatch,
                    data: Array.isArray(mismatchData)
                        ? updatedMismatchTerms
                        : {
                              ...mismatchData,
                              results: updatedMismatchTerms,
                          },
                })
            }

            return { previousAuditResults: previous, previousMismatch }
        },
        onError: (_error, _params, context) => {
            if (context?.previousAuditResults) {
                queryClient.setQueryData(
                    ['auditResults', auditId, termStatusFilter, page, pageSize],
                    context.previousAuditResults,
                )
            }

            if (context?.previousMismatch) {
                queryClient.setQueryData(
                    [
                        'attributeMismatches',
                        auditId,
                        mismatchAttributeId,
                        termStatusFilter,
                    ],
                    context.previousMismatch,
                )
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: ['auditResults', auditId],
            })
            queryClient.invalidateQueries({
                queryKey: ['attributeMismatches', auditId],
            })
            queryClient.invalidateQueries({
                queryKey: ['attributeSummary', auditId],
            })
        },
    })

    const saveReviewNoteMutation = useMutation<
        AxiosResponse<ReviewNoteResponse>,
        Error,
        SaveReviewNoteParams,
        {
            previousAuditResults?: AxiosResponse<AuditResultsResponse>
            previousMismatch?: AxiosResponse<AuditAttributeMismatchResponse>
        }
    >({
        mutationFn: (params: SaveReviewNoteParams) =>
            axiosInstance.patch(
                `/lease-audit/${auditId}/audit-results/review-note/`,
                {
                    resident_term_id: params.residentTermId,
                    attribute_id: params.attributeId,
                    note: params.note,
                },
            ),
        onMutate: async (params) => {
            const auditResultsQueryKey = [
                'auditResults',
                auditId,
                termStatusFilter,
                page,
                pageSize,
            ]
            const mismatchQueryKey = [
                'attributeMismatches',
                auditId,
                mismatchAttributeId,
                termStatusFilter,
            ]

            await queryClient.cancelQueries({ queryKey: auditResultsQueryKey })
            await queryClient.cancelQueries({ queryKey: mismatchQueryKey })

            const previous = queryClient.getQueryData<
                AxiosResponse<AuditResultsResponse>
            >(auditResultsQueryKey)
            const previousMismatch =
                mismatchAttributeId !== null
                    ? queryClient.getQueryData<
                          AxiosResponse<AuditAttributeMismatchResponse>
                      >(mismatchQueryKey)
                    : undefined

            const applyNote = (terms: AuditResidentTerm[]) =>
                terms.map((term) => {
                    if (term.id !== params.residentTermId) {
                        return term
                    }

                    return {
                        ...term,
                        attribute_values: term.attribute_values.map((value) =>
                            value.attribute_id === params.attributeId
                                ? {
                                      ...value,
                                      review_note: params.note,
                                      review_note_author_name: params.note
                                          ? value.review_note_author_name
                                          : null,
                                      review_note_at: params.note
                                          ? value.review_note_at
                                          : null,
                                  }
                                : value,
                        ),
                    }
                })

            const previousData = previous?.data
            const residentTermsToUpdate = extractResidentTerms(previousData)
            if (residentTermsToUpdate && previousData) {
                const updatedTerms = applyNote(residentTermsToUpdate)
                queryClient.setQueryData(auditResultsQueryKey, {
                    ...previous,
                    data: isPaginatedAuditResults(previousData)
                        ? { ...previousData, results: updatedTerms }
                        : { ...previousData, resident_terms: updatedTerms },
                })
            }

            const mismatchData = previousMismatch?.data
            const mismatchResults = Array.isArray(mismatchData)
                ? mismatchData
                : mismatchData?.results ?? []
            if (mismatchResults.length && previousMismatch) {
                const updatedMismatchTerms = applyNote(mismatchResults)
                queryClient.setQueryData(mismatchQueryKey, {
                    ...previousMismatch,
                    data: Array.isArray(mismatchData)
                        ? updatedMismatchTerms
                        : { ...mismatchData, results: updatedMismatchTerms },
                })
            }

            return { previousAuditResults: previous, previousMismatch }
        },
        onSuccess: (response, params) => {
            const saved = response.data
            const applySaved = (terms: AuditResidentTerm[]) =>
                terms.map((term) => {
                    if (term.id !== params.residentTermId) {
                        return term
                    }

                    return {
                        ...term,
                        attribute_values: term.attribute_values.map((value) =>
                            value.attribute_id === params.attributeId
                                ? {
                                      ...value,
                                      review_note: saved.review_note,
                                      review_note_author_name:
                                          saved.review_note_author_name,
                                      review_note_at: saved.review_note_at,
                                  }
                                : value,
                        ),
                    }
                })

            const auditResultsQueryKey = [
                'auditResults',
                auditId,
                termStatusFilter,
                page,
                pageSize,
            ]
            const current = queryClient.getQueryData<
                AxiosResponse<AuditResultsResponse>
            >(auditResultsQueryKey)
            const currentTerms = extractResidentTerms(current?.data)
            if (current?.data && currentTerms) {
                const updatedTerms = applySaved(currentTerms)
                queryClient.setQueryData(auditResultsQueryKey, {
                    ...current,
                    data: isPaginatedAuditResults(current.data)
                        ? { ...current.data, results: updatedTerms }
                        : { ...current.data, resident_terms: updatedTerms },
                })
            }

            if (mismatchAttributeId === null) {
                return
            }

            const mismatchQueryKey = [
                'attributeMismatches',
                auditId,
                mismatchAttributeId,
                termStatusFilter,
            ]
            const currentMismatch = queryClient.getQueryData<
                AxiosResponse<AuditAttributeMismatchResponse>
            >(mismatchQueryKey)
            const mismatchData = currentMismatch?.data
            const mismatchResults = Array.isArray(mismatchData)
                ? mismatchData
                : mismatchData?.results ?? []
            if (currentMismatch && mismatchResults.length) {
                const updatedMismatchTerms = applySaved(mismatchResults)
                queryClient.setQueryData(mismatchQueryKey, {
                    ...currentMismatch,
                    data: Array.isArray(mismatchData)
                        ? updatedMismatchTerms
                        : {
                              ...mismatchData,
                              results: updatedMismatchTerms,
                          },
                })
            }
        },
        onError: (_error, _params, context) => {
            if (context?.previousAuditResults) {
                queryClient.setQueryData(
                    ['auditResults', auditId, termStatusFilter, page, pageSize],
                    context.previousAuditResults,
                )
            }

            if (context?.previousMismatch) {
                queryClient.setQueryData(
                    [
                        'attributeMismatches',
                        auditId,
                        mismatchAttributeId,
                        termStatusFilter,
                    ],
                    context.previousMismatch,
                )
            }
        },
    })

    return {
        // Data
        auditResults,
        attributes: sortedAttributes,
        residentTerms,
        totalCount,
        totalPages,
        rentRollFile,
        mismatchResidentTerms,
        mismatchTotalCount,
        mismatchTotalPages,

        // Loading & Error states
        isLoading,
        error,
        attributeSummary,
        isLoadingMismatches,
        mismatchError,

        // Actions
        refetch,
        refetchMismatches,
        updateDocumentValue: updateDocumentValueMutation.mutateAsync,
        isUpdatingDocumentValue: updateDocumentValueMutation.isLoading,
        saveReviewNote: saveReviewNoteMutation.mutateAsync,
        isSavingReviewNote: saveReviewNoteMutation.isLoading,
    }
}
